import {
  AchType,
  BankOnboardingStatus,
  CapturedEventSource,
  CapturedEventStatus,
  EarningOrderStatus,
  FeeWithdrawalStatus,
  FeeWithdrawalType,
  PartnerDriverPaymentStatus,
  PaymentReviewReason,
  PaymentReviewStatus,
  RefundPaymentRail,
  RefundReferenceType,
  RefundStatus,
  TransactionPaymentType,
  TransactionReferenceType,
  UsBankDestinationType,
} from './enums'
import type { StatusLabel } from './types'

export const paymentReviewReasonLabels: Record<PaymentReviewReason, StatusLabel> = {
  [PaymentReviewReason.PossibleDuplicate]: { label: 'Possible duplicate', tone: 'warning' },
  [PaymentReviewReason.HighAmount]: { label: 'High amount', tone: 'warning' },
}

export const paymentReviewStatusLabels: Record<PaymentReviewStatus, StatusLabel> = {
  [PaymentReviewStatus.Pending]: { label: 'Pending', tone: 'warning' },
  [PaymentReviewStatus.ManuallyApproved]: { label: 'Manually approved', tone: 'success' },
  [PaymentReviewStatus.AutoApproved]: { label: 'Auto approved', tone: 'success' },
  [PaymentReviewStatus.Rejected]: { label: 'Rejected', tone: 'critical' },
}

export const partnerDriverPaymentStatusLabels: Record<PartnerDriverPaymentStatus, StatusLabel> = {
  [PartnerDriverPaymentStatus.RequiresFunding]: { label: 'Requires funding', tone: 'warning' },
  [PartnerDriverPaymentStatus.WaitingForFunding]: { label: 'Waiting for funding', tone: 'warning' },
  [PartnerDriverPaymentStatus.Paid]: { label: 'Paid', tone: 'success' },
  [PartnerDriverPaymentStatus.NotPaid]: { label: 'Not paid', tone: 'neutral' },
  [PartnerDriverPaymentStatus.Pending]: { label: 'Pending', tone: 'warning' },
  [PartnerDriverPaymentStatus.Failed]: { label: 'Failed', tone: 'critical' },
  [PartnerDriverPaymentStatus.Returned]: { label: 'Returned', tone: 'critical' },
  [PartnerDriverPaymentStatus.RolledUpToMaster]: { label: 'Rolled up to master', tone: 'neutral' },
  [PartnerDriverPaymentStatus.Voided]: { label: 'Voided', tone: 'neutral' },
}

export const transactionPaymentTypeLabels: Record<TransactionPaymentType, string> = {
  [TransactionPaymentType.Ach]: 'ACH',
  [TransactionPaymentType.Wire]: 'Wire',
  [TransactionPaymentType.Card]: 'Card',
  [TransactionPaymentType.Rtp]: 'RTP',
}

export const transactionReferenceTypeLabels: Record<TransactionReferenceType, string> = {
  [TransactionReferenceType.FeeWithdrawal]: 'Fee withdrawal',
  [TransactionReferenceType.InvoicePayment]: 'Invoice payment',
  [TransactionReferenceType.ContractorPayment]: 'Contractor payment',
  [TransactionReferenceType.Refund]: 'Refund',
}

export const refundStatusLabels: Record<RefundStatus, StatusLabel> = {
  [RefundStatus.Created]: { label: 'Created', tone: 'neutral' },
  [RefundStatus.InProgress]: { label: 'In progress', tone: 'warning' },
  [RefundStatus.Paid]: { label: 'Paid', tone: 'success' },
  [RefundStatus.Failed]: { label: 'Failed', tone: 'critical' },
}

export const refundReferenceTypeLabels: Record<RefundReferenceType, string> = {
  [RefundReferenceType.Invoice]: 'Invoice',
  [RefundReferenceType.ContractorPayment]: 'Contractor payment',
  [RefundReferenceType.ContractorEarning]: 'Contractor earning',
  [RefundReferenceType.IncomingTransaction]: 'Incoming transaction',
}

export const refundPaymentRailLabels: Record<RefundPaymentRail, string> = {
  [RefundPaymentRail.Ach]: 'ACH',
  [RefundPaymentRail.Rtp]: 'RTP',
}

export const feeWithdrawalStatusLabels: Record<FeeWithdrawalStatus, StatusLabel> = {
  [FeeWithdrawalStatus.Created]: { label: 'Created', tone: 'neutral' },
  [FeeWithdrawalStatus.InProgress]: { label: 'In progress', tone: 'warning' },
  [FeeWithdrawalStatus.Paid]: { label: 'Paid', tone: 'success' },
  [FeeWithdrawalStatus.Failed]: { label: 'Failed', tone: 'critical' },
}

export const feeWithdrawalTypeLabels: Record<FeeWithdrawalType, string> = {
  [FeeWithdrawalType.All]: 'Standard and insurance',
  [FeeWithdrawalType.Standard]: 'Standard',
  [FeeWithdrawalType.Insurance]: 'Insurance',
}

export const bankOnboardingStatusLabels: Record<BankOnboardingStatus, StatusLabel> = {
  [BankOnboardingStatus.OnboardingStarted]: { label: 'Started', tone: 'warning' },
  [BankOnboardingStatus.DataValidationInProcess]: { label: 'Validating data', tone: 'warning' },
  [BankOnboardingStatus.CustomerRecordInProcess]: {
    label: 'Creating customer record',
    tone: 'warning',
  },
  [BankOnboardingStatus.VirtualAccountSetupInProcess]: {
    label: 'Setting up virtual account',
    tone: 'warning',
  },
  [BankOnboardingStatus.OnboardingProcessCompleted]: { label: 'Completed', tone: 'success' },
  [BankOnboardingStatus.ApplicationClosed]: { label: 'Closed', tone: 'critical' },
}

export const earningOrderStatusLabels: Record<EarningOrderStatus, StatusLabel> = {
  [EarningOrderStatus.Created]: { label: 'Created', tone: 'neutral' },
  [EarningOrderStatus.Processing]: { label: 'Processing', tone: 'warning' },
  [EarningOrderStatus.Completed]: { label: 'Completed', tone: 'success' },
  [EarningOrderStatus.Failed]: { label: 'Failed', tone: 'critical' },
  [EarningOrderStatus.Voided]: { label: 'Voided', tone: 'neutral' },
  [EarningOrderStatus.FailedToCreate]: { label: 'Failed to create', tone: 'critical' },
  [EarningOrderStatus.Returned]: { label: 'Returned', tone: 'critical' },
}

export const usBankDestinationTypeLabels: Record<UsBankDestinationType, string> = {
  [UsBankDestinationType.BankAccount]: 'Bank account',
  [UsBankDestinationType.Card]: 'Card',
}

export const achTypeLabels: Record<AchType, string> = {
  [AchType.Standard]: 'Standard',
  [AchType.SameDay]: 'Same day',
}

export const capturedEventSourceLabels: Record<CapturedEventSource, string> = {
  [CapturedEventSource.PaymentProcessing]: 'Payment processing',
  [CapturedEventSource.Refund]: 'Refund',
  [CapturedEventSource.AchWebhook]: 'ACH webhook',
  [CapturedEventSource.CardWebhook]: 'Card webhook',
  [CapturedEventSource.InstantPaymentWebhook]: 'Instant payment webhook',
}

export const capturedEventStatusLabels: Record<CapturedEventStatus, StatusLabel> = {
  [CapturedEventStatus.Pending]: { label: 'Pending', tone: 'warning' },
  [CapturedEventStatus.Replayed]: { label: 'Replayed', tone: 'success' },
  [CapturedEventStatus.Discarded]: { label: 'Discarded', tone: 'neutral' },
  [CapturedEventStatus.ReplayFailed]: { label: 'Replay failed', tone: 'critical' },
}
